/**
 * Briefing API client (admin session, same origin).
 *
 * The list endpoint returns dates and headlines only; the full record (with
 * the stored HTML) is fetched by id. Both are session-only routes on the admin
 * backend under /api/briefing (nginx forwards /api/* to it).
 */

export type BriefingKind = 'daily' | 'weekly' | 'ads';

export interface BriefingListItem {
  id: string;
  kind: BriefingKind;
  date: string; // YYYY-MM-DD (America/Denver reporting date)
  headline: string;
  summary: string;
  png_url: string;
  artifact_url: string | null;
  page_url: string;
  chat_posted: boolean;
  chat_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface BriefingRecord extends BriefingListItem {
  html: string;
  links: Record<string, string>;
  png_bytes: number;
  published_by: string;
}

export class BriefingApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'BriefingApiError';
    this.status = status;
  }
}

async function request<T>(path: string): Promise<T> {
  const res = await fetch(`/api/briefing${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  let body: { success?: boolean; data?: T; error?: string; message?: string } = {};
  try {
    body = await res.json();
  } catch {
    // non-JSON (e.g. a proxy error page)
  }
  if (!res.ok || !body.success) {
    throw new BriefingApiError(body.error || body.message || `Request failed (${res.status})`, res.status);
  }
  return body.data as T;
}

export async function fetchBriefingList(kind: BriefingKind, limit = 60): Promise<BriefingListItem[]> {
  const data = await request<{ kind: string; items: BriefingListItem[] }>(`/list?kind=${kind}&limit=${limit}`);
  return data.items || [];
}

export function fetchBriefing(id: string): Promise<BriefingRecord> {
  return request<BriefingRecord>(`/${encodeURIComponent(id)}`);
}

/** Session-protected PNG download (streams from S3; never expires). */
export const pngDownloadUrl = (id: string): string => `/api/briefing/${encodeURIComponent(id)}/png`;

/** 'Fri Oct 2, 2026' for '2026-10-02'. */
export function formatBriefingDate(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  const wd = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${wd} ${md}`;
}
